/**
 * DismissEmployeeModal — exact copy of Knit's Dismiss Employee modal:
 * last day, termination reason, continuance/benefits dates, dental
 * coverage toggle, expected return, recurring-item switches, ROE notes.
 */
import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";
import { ROE_REASONS, YES_NO } from "./knitData";

const L = ({ children }) => (
  <span className="mb-0.5 block font-mono text-[9px] uppercase tracking-widest text-white/40">{children}</span>
);
const inputCls = "h-8 w-full rounded-md border border-white/10 bg-white/[0.03] px-2 text-sm text-white/90 focus:border-[#1a3fd1] focus:outline-none";

export default function DismissEmployeeModal({ employee, onClose, onDismissed }) {
  const [f, setF] = useState({
    last_day: new Date().toISOString().slice(0, 10),
    termination_reason: "",
    salary_continuance_end: "",
    benefits_end: "",
    dental_until_year_end: "No",
    expected_return: "",
    disable_recurring_incomes: true,
    disable_recurring_deductions: true,
    exclude_from_payrolls: true,
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const submit = async () => {
    if (!f.last_day) { toast.error("Employee Last Day is required"); return; }
    setBusy(true);
    try {
      await api.post(`/employees/${employee.employee_id}/dismiss`, f);
      toast.success(`${employee.name} dismissed — removed from payroll`);
      onDismissed?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not dismiss employee");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[160] flex items-center justify-center p-4" data-testid="dismiss-modal">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl border border-white/10 bg-[#101014] p-5">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white">Dismiss Employee</h3>
          <button onClick={onClose} aria-label="Close" className="rounded p-1 text-white/50 hover:bg-white/10 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
        <div className="mb-4 space-y-1.5 text-[11px] leading-4 text-white/50">
          <p>You are about to dismiss {employee.name}!</p>
          <p>Dismissing an employee removes the employee from payrolls beyond their last payment date.</p>
          <p>They will still appear in payrolls that cover the employee's termination date, however the default regular hours will be set to 0 unless they worked the full period.</p>
          <p>The employee's information will still remain in the system for tax purposes.</p>
          <p>This action is not reversible.</p>
        </div>
        <div className="space-y-3">
          <label className="block"><L>*Employee Last Day</L>
            <input type="date" value={f.last_day} onChange={set("last_day")} data-testid="dismiss-last-day" className={inputCls} /></label>
          <label className="block"><L>*Termination Reason</L>
            <select value={f.termination_reason} onChange={set("termination_reason")} data-testid="dismiss-reason"
              className={`${inputCls}`}>
              <option value="">Please select</option>
              {ROE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select></label>
          <label className="block"><L>Salary Continuance End Date</L>
            <input type="date" value={f.salary_continuance_end} onChange={set("salary_continuance_end")} data-testid="dismiss-salary-end" className={inputCls} /></label>
          <label className="block"><L>Insurance/Benefits coverage end date</L>
            <input type="date" value={f.benefits_end} onChange={set("benefits_end")} data-testid="dismiss-benefits-end" className={inputCls} /></label>
          <label className="block"><L>If the employee will have dental coverage until Dec 31 of {new Date().getFullYear()}</L>
            <select value={f.dental_until_year_end} onChange={set("dental_until_year_end")} data-testid="dismiss-dental"
              className={`${inputCls}`}>
              {YES_NO.map((o) => <option key={o} value={o}>{o}</option>)}
            </select></label>
          <label className="block"><L>Expected Return Date</L>
            <input type="date" value={f.expected_return} onChange={set("expected_return")} data-testid="dismiss-return-date" className={inputCls} /></label>
          <div className="space-y-1.5 pt-1">
            {[
              ["disable_recurring_incomes", "Disable recurring incomes", "dismiss-opt-incomes"],
              ["disable_recurring_deductions", "Disable recurring deductions", "dismiss-opt-deductions"],
              ["exclude_from_payrolls", "Exclude from regular payrolls unless they fully cover the employee's last day of employment", "dismiss-opt-exclude"],
            ].map(([k, label, tid]) => (
              <label key={k} className="flex items-start gap-2 text-[11px] text-white/70">
                <input type="checkbox" checked={f[k]} data-testid={tid}
                  onChange={(e) => setF((p) => ({ ...p, [k]: e.target.checked }))}
                  className="mt-0.5 h-3.5 w-3.5 accent-amber-500" />
                {label}
              </label>
            ))}
          </div>
          <label className="block"><L>Additional notes</L>
            <textarea value={f.notes} onChange={set("notes")} rows={3} data-testid="dismiss-notes"
              placeholder="Type additional notes here, they will carry over to the ROE."
              className="w-full rounded-md border border-white/10 bg-white/[0.03] px-2 py-1.5 text-sm text-white/90 focus:border-[#1a3fd1] focus:outline-none" /></label>
        </div>
        <div className="mt-4 flex gap-2">
          <button onClick={submit} disabled={busy} data-testid="dismiss-submit"
            className="rounded-md bg-red-700 px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-widest text-white hover:bg-red-600 disabled:opacity-50">
            {busy ? "Dismissing…" : "Dismiss Employee"}
          </button>
          <button onClick={onClose} data-testid="dismiss-cancel"
            className="rounded-md border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-white/70 hover:text-white">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
